import React, { useState, useEffect, useLayoutEffect, useReducer } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'

import { Helmet } from 'react-helmet'

import DOMPurify from 'dompurify'

import { BrPageContext, BrComponent, BrPage } from '@bloomreach/react-sdk'

import { ProductLanding } from './ProductLanding.jsx'
import OnRouteChange from '../../spa/utility/hooks/routing.js'
import Footer from '../../spa/components/footer/Footer.jsx'
import { getBaseURL } from '../../global/environments/index.js'

import {
  Banner,
  BlogArticle,
  BlogSimpleArticleList,
  Divider,
  ArticleList,
  ArticleDetail,
  Alert,
  CampaignHeader,
  Navigation,
  Search,
  Form,
  Social,
  Metamenu,
  Metadata,
  TextBlock,
  Wholesaler,
  ContactUsForm,
  WholesalerContactForm,
  ResourceArticle,
  FundCards,
  FundLiterature,
  CartOrder,
  Tabs,
  ProductListing,
  OneTrust,
  ProductDetail,
  BlogArticleList,
  Disclosures,
  BlogAuthor,
  BlogCta,
  Subsection,
  RiaLiterature,
  FormsAndApplications,
  AdvisorLogin,
  AdvisorEmailChange,
  AdvisorRegister,
  AdvisorForgotPassword,
  MyProfile,
  ProductHeader,
  Accordion,
  SimpleEntryComponent,
  Statistics,
  HeroBanner,
} from '../components'

import {
  getDebugConfig,
  getLocalPath,
  LayoutContext,
  NavigationContext,
  reduceNavigationItems,
  GlobalProvider,
} from '../utility'

import { fetchData } from '../data'

const getBreConfig = () => {
  const _debugConfig = getDebugConfig()

  axios.defaults.withCredentials = true

  const _configuration = {
    path: _debugConfig.PATH,
    endpoint: _debugConfig.ENDPOINT,
    origin: _debugConfig.ORIGIN,
    debug: _debugConfig.DEBUG,
    authorizationHeader: 'HST-Authorization',
    httpClient: axios,
  }

  return _configuration
}

export default function Route() {
  const location = useLocation()
  const _mapping = {
    Banner2: Banner,
    BlogSimpleArticleList,
    BlogArticleList,
    BlogArticle,
    Divider,
    Article2: ArticleDetail,
    ArticleList2: ArticleList,
    Alert2: Alert,
    Search2: Search,
    Form2: Form,
    Social2: Social,
    Metamenu2: Metamenu,
    Metadata: Metadata,
    TextBlock,
    Wholesaler,
    ContactUsForm,
    WholesalerContactForm,
    CampaignHeader,
    ResourceArticle,
    FundCards,
    FundLiterature,
    CartOrder,
    Tabs,
    ProductListing,
    OneTrust,
    ProductDetail,
    Disclosures,
    BlogAuthors: BlogAuthor,
    Subsection2: Subsection,
    RiaLiterature,
    FormsAndApplications,
    AdvisorLogin,
    AdvisorEmailChange,
    AdvisorRegister,
    AdvisorForgotPassword,
    MyProfile,
    BlogCta,
    ProductHeader,
    Accordion2: Accordion,
    SimpleEntryComponent,
    Statistics,
    HeroBanner,
  }

  const _config = getBreConfig()

  const [navigationItems, setNavigationItems] = useReducer(
    reduceNavigationItems,
    []
  )
  const [isMenuVisible] = useState(false)

  const sanitize = DOMPurify.sanitize

  useEffect(() => {
    setNavigationItems({ type: 'clear' })
  }, [location.pathname])

  useEffect(() => {
    // runs after page load, checks for elements that need analytics tags added
    const addAnalyticsTags = async () => {
      const analyticsClass = 'ckeditor-content-selector-analytics'
      const contentToTag = document.getElementsByClassName(analyticsClass)

      if (contentToTag?.length > 0) {
        const itemsUrl = `${getLocalPath()}/halapi/documents?_scope=/content/documents/administration/cti-common/&_expr=jcr:contains(@resourcebundle:id,%20%27ckeditor.analytics.tags%27)`
        const data = await fetchData(itemsUrl)
        const tagValues = data?._embedded?.documents?.[0]?.bundles?.default

        contentToTag.forEach((item) => {
          const innerChild = getInnermostChild(item)
          const tagKeys = Object.keys(tagValues)
          const tagValue =
            tagValues[tagKeys[tagKeys.indexOf(innerChild.innerText)]]

          if (tagValue) {
            innerChild.setAttribute('data-analytics', tagValue)
          } else {
            item.classList.remove(analyticsClass)
          }
        })
      }

      function getInnermostChild(element) {
        const children = element.children

        if (children.length > 0) {
          for (const child of children) {
            const innerChild = getInnermostChild(child)

            if (innerChild) {
              return innerChild
            }
          }
        } else {
          return element
        }
      }
    }

    window.addEventListener('load', addAnalyticsTags)

    return () => {
      window.removeEventListener('load', addAnalyticsTags)
    }
  }, [])

  useLayoutEffect(() => {
    // Fix scroll issue e.g. clicking Terms and Conditions from Home page puts you in middle of page when it should be at the top
    if (location?.state?.doScroll) {
      setTimeout(() => {
        window.scroll({
          top: 0,
          left: 0,
        })
      }, 200)
    }
  }, [location.pathname])

  return (
    <>
      <OnRouteChange location={location} />
      <BrPage configuration={_config} mapping={_mapping}>
        {!isMenuVisible && (
          <main className="u-posRelative">
            <BrPageContext.Consumer>
              {(page1) => {
                const pageTitle =
                  page1.getTitle() || page1.getDocument()?.model?.data?.title
                return (
                  <GlobalProvider>
                    <Helmet>
                      {pageTitle && (
                        <title>
                          {`${pageTitle} | Columbia Threadneedle Investments US`}
                        </title>
                      )}
                      <link
                        rel="canonical"
                        href={sanitize(`${getBaseURL()}${page1.getUrl()}`)}
                      />
                    </Helmet>
                    {Object.keys(page1.model.page).find(
                      (entry) => page1.model.page[entry].name === 'tabOne'
                    ) ? (
                      // PRODUCT PAGES
                      <ProductLanding page={page1} />
                    ) : Object.keys(page1.model.page).find(
                        (entry) => page1.model.page[entry].name === 'right'
                      ) ? (
                      // DOUBLE COLUMN
                      <div>
                        <BrComponent path="above" />
                        <div className="u-flex">
                          <div
                            className={page1.isPreview() ? 'u-flexGrow2' : ''}
                          >
                            <BrComponent path="main" />
                          </div>
                          <div
                            className={`u-marginLeft5gu ${
                              page1.isPreview() ? 'u-flexGrow1' : ''
                            }`}
                          >
                            <LayoutContext.Provider
                              value={{
                                container: 'right',
                              }}
                            >
                              <BrComponent path="right" />
                            </LayoutContext.Provider>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // SINGLE COLUMN
                      <div className="u-flex u-flexCol">
                        <NavigationContext.Provider
                          value={{
                            navigationItems: navigationItems,
                            setNavigationItems: setNavigationItems,
                          }}
                        >
                          <Navigation />
                          <BrComponent path="main">
                            <div
                              className={`u-flexAuto ${
                                page1.isPreview() ? 'u-sizeFull' : ''
                              }`}
                            >
                              <BrComponent />
                            </div>
                          </BrComponent>
                        </NavigationContext.Provider>
                      </div>
                    )}
                  </GlobalProvider>
                )
              }}
            </BrPageContext.Consumer>
            <BrComponent path="footer">
              <Footer />
            </BrComponent>
          </main>
        )}
      </BrPage>
    </>
  )
}
